import settings from "./settings";

type LanguageConfig = {
  label: string;
  key: string;
  locale: string;
};

const availableLanguages = settings.I18N_AVAILABLE_LANGUAGES as LanguageConfig[];

const availableLanguageKeys = new Set(
  availableLanguages.map((language) => language.key.toLowerCase())
);

const defaultLanguageKey = settings.I18N_DEFAULT_LOCALE.toLowerCase();
const fallbackLanguageKey = settings.I18N_FALLBACK_LOCALE.toLowerCase();

export const getAvailableLanguages = (): LanguageConfig[] => {
  return availableLanguages;
};

export const getAvailableLanguageKeys = (): string[] => {
  return availableLanguages.map((language) => language.key.toLowerCase());
};

export const getDefaultLanguageKey = (): string => {
  return defaultLanguageKey;
};

export const getFallbackLanguageKey = (): string => {
  if (availableLanguageKeys.has(fallbackLanguageKey)) {
    return fallbackLanguageKey;
  }
  if (availableLanguageKeys.has(defaultLanguageKey)) {
    return defaultLanguageKey;
  }
  return "en";
};

export const isSupportedLanguageKey = (language?: string): boolean => {
  if (!language) {
    return false;
  }
  return availableLanguageKeys.has(String(language).toLowerCase());
};

const getLanguageFromLocaleCode = (localeLike: string): string | null => {
  const normalized = localeLike.replace(/-/g, "_").toLowerCase();
  const matched = availableLanguages.find(
    (language) => language.locale.toLowerCase() === normalized
  );
  if (matched) {
    return matched.key.toLowerCase();
  }

  const prefix = normalized.split("_")[0];
  if (availableLanguageKeys.has(prefix)) {
    return prefix;
  }
  return null;
};

export const normalizeLanguageKey = (
  input?: string,
  fallback = getFallbackLanguageKey()
): string => {
  const raw = String(input || "").trim();
  if (!raw) {
    return fallback;
  }

  const lower = raw.toLowerCase();
  if (availableLanguageKeys.has(lower)) {
    return lower;
  }

  const localeKey = getLanguageFromLocaleCode(raw);
  if (localeKey) {
    return localeKey;
  }

  return fallback;
};

export const getLocaleCodeByLanguageKey = (
  languageKey?: string,
  fallbackLocale = "en_US"
): string => {
  const normalizedKey = normalizeLanguageKey(languageKey);
  const matched = availableLanguages.find(
    (language) => language.key.toLowerCase() === normalizedKey
  );
  if (matched?.locale) {
    return matched.locale;
  }

  const fallbackKey = getFallbackLanguageKey();
  const fallbackLanguage = availableLanguages.find(
    (language) => language.key.toLowerCase() === fallbackKey
  );
  return fallbackLanguage?.locale || fallbackLocale;
};

export const toLanguageStorageValue = (languageKey?: string): string => {
  return normalizeLanguageKey(languageKey).toUpperCase();
};
